import React from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { createPortal } from 'react-dom';

interface CookiesPolicyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const CookiesPolicyModal: React.FC<CookiesPolicyModalProps> = ({ isOpen, onClose }) => {
    // Prevent scrolling when modal is open
    useScrollLock(isOpen);

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-orange-200/50 animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex justify-between items-center p-6 sm:p-8 border-b border-orange-200/30 shrink-0">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900">Cookies Policy</h2>
                        <p className="text-gray-500 text-sm mt-1">Last updated: March 1, 2026</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-900 text-2xl font-bold transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="overflow-y-auto p-6 sm:p-8 space-y-8">
                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">1. What Are Cookies?</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and to provide information to the owners of the site.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Cookies</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>We use cookies for several purposes:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>
                                    <strong className="text-gray-800">Necessary Cookies: </strong>
                                    Required for core functionality such as authentication, security, and session management.
                                </li>
                                <li>
                                    <strong className="text-gray-800">Preference Cookies: </strong>
                                    Used to remember your settings and improve your experience.
                                </li>
                            </ul>

                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Managing Cookies</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience, since it will no longer be personalized to you.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Third-Party Cookies</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>
                                We do not use third-party cookies for advertising or marketing purposes.
                                Any cookies used are strictly necessary for the operation and security of our services.
                            </p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Updates to This Policy</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>We may update our Cookie Policy from time to time. We encourage you to periodically review this page for the latest information on our privacy practices.</p>
                        </div>
                    </section>

                    <section>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Contact Us</h3>
                        <div className="space-y-3 text-gray-600">
                            <p>If you have any questions about our use of cookies, please contact us:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Email: support@focuzon.com</li>
                            </ul>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-orange-200/30 bg-orange-50/50 rounded-b-3xl">
                    <p className="text-center text-gray-500 text-sm">
                        By using our services, you agree to our use of cookies.
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CookiesPolicyModal;
